<template>
  <div class="tender-detail-page">
    <div v-if="!tender || pending" class="tender-header"></div>
    <TenderHeader
      v-else-if="tender && !pending"
      :tender="tender"
      @navigate="navigate"
      :backToMode="backToMode"
      ref="tenderHeader"
    />
    <BaseLoadingBar v-if="pending" class="loading-bar" />
    <div v-else-if="!tender" class="tender-detail-page__error">
      {{ $t('tender-details.load-failed') }}
    </div>
    <div v-else class="tender-detail-page__body">
      <div class="tender-detail-page__top-section">
        <TenderKeywords :keywords="matchedKeywords" :loading="matchesPending" />
        <TenderThumbsRating :tender="tender" @navigate="navigate" />
      </div>
      <TenderTitle :tender="tender" />

      <div class="tender-detail-page__container">
        <div>
          <div class="tender-detail-page__flex">
            <TenderInformation :tender="tender" />
          </div>
          <TenderDescription :tender="tender" />
          <TenderLegal :tender="tender" />
        </div>
        <div class="tender-detail-page__classification-lvl">
          <TenderOpportunities :tender="tender" v-if="tenantHasOpportunitiesAccess()" />
          <TenderBidders @action-succeeded="onActionSucceeded" />
          <TenderDocuments :tender="tender" />
          <TenderClassification :classifications="tender.classifications" />
        </div>
      </div>
    </div>
    <TenderBottomNav
      v-if="tender && (nextTender || previousTender)"
      :nextTender="nextTender"
      :previousTender="previousTender"
      :tender="tender"
      @change-next="changeNext"
      @change-previous="changePrevious"
    />
  </div>
</template>

<script>
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import TenderBidders from '@/components/page-tender-details/TenderBidders';
import TenderBottomNav from '@/components/page-tender-details/TenderBottomNav';
import TenderClassification from '@/components/page-tender-details/TenderClassification';
import TenderDescription from '@/components/page-tender-details/TenderDescription';
import TenderHeader from '@/components/page-tender-details/TenderHeader';
import TenderInformation from '@/components/page-tender-details/TenderInformation';
import TenderKeywords from '@/components/page-tender-details/TenderKeywords';
import TenderLegal from '@/components/page-tender-details/TenderLegal';
import TenderOpportunities from '@/components/page-tender-details/TenderOpportunities';
import TenderTitle from '@/components/page-tender-details/TenderTitle';
import TenderDocuments from '@/components/page-tender-details/TenderDocuments';
import tenderScoreMixin from '@/mixins/tender-score-mixin';
import TenderThumbsRating from '@/components/page-tender-details/TenderThumbsRating.vue';

import { tenantHasOpportunitiesAccess, tenantHasTenderScoringAccess } from '@/util/module-access-util';
import { Modes } from '@/models/modes';
import { bus } from '@/main';
import { isNavigationFailure } from 'vue-router';
import { mapActions, mapGetters } from 'vuex';
import { paths } from '@/util/paths';

export default {
  name: 'PageTenderDetails',
  components: {
    TenderThumbsRating,
    BaseLoadingBar,
    TenderBidders,
    TenderBottomNav,
    TenderClassification,
    TenderDescription,
    TenderHeader,
    TenderInformation,
    TenderKeywords,
    TenderLegal,
    TenderOpportunities,
    TenderTitle,
    TenderDocuments,
  },
  mixins: [tenderScoreMixin],
  props: {
    id: String,
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('tenant', ['tenant']),
    ...mapGetters('tenantModules', ['tenantModules']),
    ...mapGetters('modes', ['mode']),
    ...mapGetters('tender', ['tender', 'pending', 'matchedKeywords', 'error', 'matchesPending', 'requestedTenderId']),
    ...mapGetters('tenders', [
      'tenders',
      'getPreviousTender',
      'getNextTender',
      'previousPage',
      'nextPage',
      'page',
      'bufferTender',
      'nextTenders',
      'previousTenders',
    ]),
    ...mapGetters('searchFilters', ['searchFilters']),
    ...mapGetters('exploreSearchFilters', { exploreKeywords: 'keywords', exploreSearchFilters: 'searchFilters' }),
    parameters() {
      return this.isExploreMode ? this.exploreSearchFilters : this.searchFilters;
    },
    isArchived() {
      return this.tender && this.tender.tenantTender && this.tender.tenantTender.archived;
    },
    isMacOS() {
      return navigator.userAgent.toLowerCase().indexOf('mac') >= 0;
    },
    // The buffer, a tender of the next or previous page, continues the list from its edge only: a
    // tender opened from outside the list (a related source, a link) has no next or previous one.
    nextTender() {
      if (!this.tender) return null;
      if (this.getNextTender(this.tender.id)) return this.getNextTender(this.tender.id);
      else if (
        this.bufferTender &&
        this.nextPage &&
        this.tenderIndex >= 0 &&
        this.tenderIndex === this.tenders.length - 1
      ) {
        return this.bufferTender;
      }
      return null;
    },
    previousTender() {
      if (!this.tender) return null;
      if (this.getPreviousTender(this.tender.id)) return this.getPreviousTender(this.tender.id);
      else if (this.bufferTender && this.previousPage && this.tenderIndex === 0) return this.bufferTender;
      return null;
    },
    tenderIndex() {
      return this.tenders.findIndex((tender) => tender.id === this.tender.id);
    },
    // Measured from the end of the list, not of a 25-tender page: on phones the list holds
    // every page loaded so far.
    needNextTenders() {
      const index = this.tenders.findIndex((tender) => tender.id === this.tender.id);
      return index !== -1 && index >= this.tenders.length - 6 && this.nextPage !== null && !this.nextTenders;
    },
    needPreviousTenders() {
      const index = this.tenders.findIndex((tender) => tender.id === this.tender.id);
      return index < 5 && this.previousPage !== null && !this.previousTenders;
    },
    readyToNextPage() {
      const index = this.tenders.findIndex((tender) => tender.id === this.tender.id);
      return index !== -1 && index === this.tenders.length - 1 && this.nextPage !== null && this.nextTenders;
    },
    readyToPreviousPage() {
      const index = this.tenders.findIndex((tender) => tender.id === this.tender.id);
      return index === 0 && this.previousPage !== null && this.previousTenders;
    },
    isExploreMode() {
      return this.mode === Modes.EXPLORE.name;
    },
  },
  methods: {
    ...mapActions('tender', ['getTender', 'read']),
    ...mapActions('biDashboard', { getBiTender: 'getTender', getAllCompetitors: 'getAllCompetitors' }),
    ...mapActions('tenders', ['loadNextTenders', 'loadPreviousTenders', 'changePage']),
    ...mapActions('tenderOpportunities', ['getTenderOpportunities']),
    ...mapActions('opportunities', { resetOpportunities: 'RESET' }),
    tenantHasOpportunitiesAccess,
    tenantHasTenderScoringAccess,
    navigate() {
      const id = this.tender.id;
      const nextTender = this.getNextTender(id);
      const previousTender = this.getPreviousTender(id);
      const previousPageTender = this.previousTender;

      if (nextTender) {
        this.changeTender(nextTender.id);
      } else if (previousTender) {
        this.changeTender(previousTender.id);
      } else if (!previousTender && previousPageTender) {
        this.changePrevious(this.previousTender.id);
      } else {
        this.backToMode();
      }
    },
    changeNext(id) {
      if (this.pending) return;
      if (this.readyToNextPage) this.changePage({ page: this.nextPage });
      this.changeTender(id);
    },
    changePrevious(id) {
      if (this.pending) return;
      if (this.readyToPreviousPage) this.changePage({ page: this.previousPage });
      this.changeTender(id);
    },
    loadTender(id) {
      // All requests start in parallel; only the tender itself gates the first render.
      const tenderLoaded = this.getTender({
        id,
        keywords: this.isExploreMode ? this.exploreKeywords : null,
        explore: this.isExploreMode,
      });

      // A failure here only affects its own panel, not the page.
      this.getBiTender({ id }).catch((error) => console.error('Tender details: BI data failed to load', error));
      this.getAllCompetitors().catch((error) => console.error('Tender details: competitors failed to load', error));
      if (tenantHasOpportunitiesAccess()) {
        this.getTenderOpportunities({ tenderId: id }).catch((error) =>
          console.error('Tender details: opportunities failed to load', error),
        );
      }

      return tenderLoaded.then(() => {
        // Superseded by a newer request in the meantime (a cancelled request resolves silently, and
        // the displayed tender may still be this one if it was being reloaded).
        if (this.requestedTenderId !== id || !this.tender || this.tender.id !== id) return;

        // Not awaited: marking as read must not delay the render.
        this.read({ id }).catch((error) => console.error('Tender details: mark as read failed', error));
        bus.$emit('last-viewed-tender-id', id);
      });
    },
    changeTender(id) {
      if (!id || this.requestedTenderId === id) return;

      this.$track.tenderChange();

      // Navigate first; the `id` watcher loads the tender.
      this.$router.push({ name: 'tender-details', params: { id } }).catch((error) => {
        // Duplicate/aborted navigations are expected when arrowing quickly.
        if (!isNavigationFailure(error)) this.handleError(error);
      });
    },
    getNextTenders() {
      this.loadNextTenders({ searchFilters: this.parameters, page: this.nextPage });
    },
    getPreviousTenders() {
      this.loadPreviousTenders({ searchFilters: this.parameters, page: this.previousPage });
    },
    // Vuetify tears its dialog down synchronously while handling the same keystroke, so by the time
    // any bubbling listener runs there is nothing left to detect. This runs in the capture phase,
    // which is early enough to still see it.
    handleKeydown() {
      this.hadDialogOpen = !!document.querySelector('.v-dialog--active');
    },
    handleKeyup(event) {
      // The keystroke that dismissed a dialog must not also act on the page behind it.
      const hadDialogOpen = this.hadDialogOpen;
      this.hadDialogOpen = false;
      if (hadDialogOpen) return;

      const keyCode = event.keyCode || event.charCode;

      // left arrow ←
      if (keyCode === 37 && this.previousTender) {
        this.changePrevious(this.previousTender.id);
      }

      // right arrow →
      if (keyCode === 39 && this.nextTender) {
        this.changeNext(this.nextTender.id);
      }

      // Esc. keyup
      if (keyCode === 27) this.backToMode();

      // delete - backspace (for macOS only)
      if ((keyCode === 46 || (keyCode === 8 && this.isMacOS)) && !this.isArchived) {
        this.$refs.tenderHeader.onArchiveClick();
      }
    },
    backToMode() {
      const mode = Modes[`${this.mode}`];
      const url = mode && mode.url ? mode.url : { name: 'tenders-inbox' };
      this.$router.push(url);
    },
    handleError(error) {
      if (!error.response) this.$router.replace(paths.notFound);

      // If a not found or forbidden error occurs, redirect to not-found page
      if (error.response.status === 400 || error.response.status === 404) {
        this.$router.replace(paths.notFound);
      } else if (error.response.status === 403) {
        if (error.response.data) {
          // If the error is a forbidden error and the source is a platform, redirect to forbidden-platform page
          const source = error.response.data.message.match(/'([^']+)'/)[1];
          if (source) {
            this.$router.replace({ name: 'forbidden-platform', params: { platform: source } });
            return;
          }
        }
        this.$router.replace(paths.notFound);
      } else {
        // Otherwise show an error toast
        console.error(error);
        this.$toast.error(error);
        throw error;
      }
    },
    onActionSucceeded() {
      this.getBiTender({ id: this.id }).catch((error) =>
        console.error('Tender details: BI data failed to load', error),
      );
      // Cached; refetched only if a competitor changed.
      this.getAllCompetitors().catch((error) => console.error('Tender details: competitors failed to load', error));
    },
  },
  async created() {
    try {
      await this.loadTender(this.id);

      document.addEventListener('keydown', this.handleKeydown, true);
      document.addEventListener('keyup', this.handleKeyup);
    } catch (error) {
      this.handleError(error);
    }
  },
  watch: {
    async id(id) {
      // Compare with the requested tender, not the displayed one: the previous tender stays on
      // screen while a load is pending, and a Back to it must start (and win) its own request.
      if (this.requestedTenderId === id) return;

      try {
        await this.loadTender(id);
      } catch (error) {
        this.handleError(error);
      }
    },
    tender() {
      if (this.tender && this.needNextTenders) this.getNextTenders();
      else if (this.tender && this.needPreviousTenders) this.getPreviousTenders();
    },
  },
  async destroyed() {
    document.removeEventListener('keydown', this.handleKeydown, true);
    document.removeEventListener('keyup', this.handleKeyup);
  },
};
</script>

<style lang="scss">
.tender-detail-page {
  &__body {
    overflow-y: hidden !important;
    display: flex;
    flex: auto;
    flex-direction: column;
    margin: 32px 32px 128px 32px;

    // Phones: 16px from the screen's edges and inside the cards, the title a phone's size.
    @include screen-below('tablet') {
      margin: $phone-page-gutter $phone-page-gutter 128px;

      .tender-title,
      .tender-information,
      .tender-description,
      .tender-legal,
      .tender-opportunities,
      .tender-bidders,
      .tender-documents,
      .tender-classification {
        padding: 16px;
      }

      .tender-title__title {
        font-size: 22px;
        line-height: 1.25;
      }
    }
  }

  &__error {
    margin: 32px;
    color: $color-neutral;
    font-size: $font-size-small;
  }

  &__flex {
    display: flex;
    flex-direction: row;
    margin-right: 1rem;
    margin-bottom: 1rem;
  }

  &__top-section {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0 4px 16px 4px;
  }

  &__container {
    display: grid;
    grid-template-columns: 60% 40%;
  }

  &__classification-lvl {
    height: 100%;

    .content {
      color: $color-normal-pale;
      font-size: 0.875rem;
      line-height: 1.43;
      word-break: break-word;
    }
  }
  .title {
    color: $color-normal;
    display: flex;
    font-size: 20px;
    font-weight: 800;
    justify-content: flex-start;
  }

  .subtitle {
    color: $color-normal;
    font-size: $font-size-small;
    font-weight: bold;
    letter-spacing: normal;
    line-height: 1.43;
    margin-bottom: 0.3rem;
    padding-top: 1rem;
  }

  @media screen and (max-width: 1080px) {
    &__container {
      display: block;
    }

    &__flex {
      margin-right: 0;
    }

    .title {
      font-size: $font-size-xbigger;
    }
  }
}
</style>
